import { useEffect, useState } from "react";
import { Query } from "@/utils/react-query/Queries";
import useDebounce from "@/utils/hooks/useDebounce";
import { TouchableOpacity, View } from "react-native";
import { Text } from "react-native";
import { LegendList } from "@legendapp/list";
import Avatar from "@/components/People/Avatar";
import { ActivityIndicator } from "react-native-paper";
import { StyleSheet } from "react-native-unistyles";
import Unicon from "@/components/General/Unicon";

const PickerList = ({type, position, items = null, term, onConfirm, onDismiss}) => {
    const [filteredData, setFilteredData] = useState([]);
    // term updates per keystroke from mention/link detection — debounce the
    // query key while keeping raw term for the client-side filter below.
    const debouncedTerm = useDebounce(term, 350);

    const {data: peopleData, isFetching: peopleFetching} = Query.people.all({searchTerm: debouncedTerm, options: {enabled: type == "link" && items == null}})
    // const {data: tagsData, isFetching: tagsFetching} = Query.tags.all({options: {enabled: type == "tag" && items == null}})
    const {data: linksData, isFetching: linksFetching} = Query.links.all({options: {enabled: type == "link" && items == null}})


    useEffect(() => {
        const data = items != null ? items : (type == "mention" ? peopleData?.pages?.flatMap(p => p.data) : (type == "tag" ? /*tagsData*/[] : linksData))

        if(term && data?.length > 0){
            if(type == "mention"){
                items != null ? setFilteredData(data?.filter(d => d.fullName?.toLowerCase().includes(term.toLowerCase()))) : setFilteredData(data)
            }
            if(type == "tag"){
                setFilteredData(data?.filter(d => d.name?.toLowerCase().includes(term.toLowerCase())))
            }
            else if(type == "link"){
                setFilteredData(data?.filter(d => d.name?.toLowerCase().includes(term.toLowerCase())))
            }
        }
    }, [term, peopleData, linksData])

    const Item = ({item}) => {
        return <TouchableOpacity onPress={() => {
            onConfirm({text: item.name || item.fullName, id: item.id, type: type, url: item.url});
            onDismiss();
        }} style={styles.item}>
            <View>
            {type == "mention" 
                ? <View style={styles.space}><Avatar profile={item} size={22} disableBorder /></View>
                : <View style={styles.space}><Unicon name="link" size={20} color={"textSecondary"} /></View>}
            </View>
            <View>
                <Text style={styles.itemText}>{item.name || item.fullName || (item.firstName + " " + item.lastName)}</Text>
                {type == "link" && <Text style={styles.itemLink}>{item.url}</Text>}
            </View>
        </TouchableOpacity> 
    }

    return (
        <View style={styles.picker(position)}>
            <View style={styles.listContainer}>
                <LegendList recycleItems keyExtractor={(item) => item.id} 
                    estimatedItemSize={50}
                    data={(peopleFetching || linksFetching) ? [] : filteredData}
                    renderItem={Item}
                    ListEmptyComponent={(peopleFetching || linksFetching) ? <View style={styles.loader}><ActivityIndicator /></View> : <Text style={styles.emptyText}>{!term ? `Start typing to ${type == "mention" ? "mention someone." : type == "tag" ? "reference a tag." : "reference a link"}` : "No results found"}</Text>}
                    keyboardShouldPersistTaps="handled"
/>
            </View>
        </View>
    )
}

const delimiter = {
    start: '<<!#',
    end: '#!>>'
}

const styles = StyleSheet.create(({colors, fonts, shadows}) => ({
    space: {
        marginRight: 10,
    },
    picker: (position) => ({
        position: "absolute",
        zIndex: 10,
        top: position.y,
        left: position.x,
        height: 150,
        width: 300,
        borderRadius: 10,
        backgroundColor: colors.surface,
        boxShadow: shadows.elevated,
        borderWidth: 1,
        borderColor: colors.border,
    }),
    loader: {
        width: "100%",
        height: 150,
        alignItems: "center",
        justifyContent: "center",
    },
    emptyText: {
        color: colors.textSecondary,
        textAlign: "center",
        marginVertical: 10,
        fontSize: 13,
        fontFamily: fonts.body,
    },
    item: {
        padding: 10, 
        marginVertical: 4, 
        borderBottomWidth: 1, 
        borderColor: colors.border, 
        flexDirection: "row",  
        alignItems: "center",
    },
    itemText: {
        fontSize: 13, 
        color: colors.text,
        fontFamily: fonts.body,
    },
    itemLink: {
        fontSize: 11, 
        color: colors.textSecondary, 
        fontStyle: "italic",
    },
    listContainer: {
        flex: 1, 
        width: "100%", 
        overflow: "hidden",
    }
}))

export {PickerList, delimiter}
